<template>
  <el-form
    ref="ruleFormRef"
    :model="form"
    :rules="rules"
  >
    <el-row
      :gutter="10"
      style="justify-content: space-between"
    >
      <el-col :span="6">
        <el-form-item>
          <custom-data
            :data="form.Codigo"
            :spanColumn="12"
            label="Código"
          />
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item>
          <custom-input
            v-model="form.Clave"
            :spanColumn="24"
            label="Clave búsqueda"
            :settings="{
              placeholder: 'Clave búsqueda',
              disabled: disabled,
            }"
          />
        </el-form-item>
      </el-col>
    </el-row>
    <el-form-item prop="Descripcion">
      <custom-input
        ref="descripcionInputRef"
        v-model="form.Descripcion"
        :spanColumn="24"
        label="Descripción"
        :settings="{
          placeholder: 'Descripción',
          disabled: disabled,
        }"
      />
    </el-form-item>
    <el-form-item prop="CodigoAdicional">
      <custom-input
        ref="codigoAdicionalInputRef"
        v-model="form.CodigoAdicional"
        :spanColumn="24"
        label="Codigo Adicional"
        :settings="{
          placeholder: 'Codigo Adicional',
          disabled: disabled,
        }"
      />
    </el-form-item>
    <el-row :gutter="15">
      <el-col
        :span="21"
        style="padding-left: 0px"
      >
        <el-form-item prop="Deposito">
          <custom-select
            v-model="form.Deposito"
            :spanColumn="24"
            label="Depósito"
            :settings="{
              placeholder: 'Depósito',
              disabled: disabled,
              options: depositos,
            }"
          />
        </el-form-item>
      </el-col>
      <el-col :span="2">
        <div style="height: 24px">&nbsp;</div>
        <el-button
          type="default"
          size="default"
          :icon="IconAdd"
          @click="handleClickAddDeposito"
        />
      </el-col>
    </el-row>
    <el-form-item>
      <additional-settings-buttons
        :disabled="disabled"
        @add="handleClickAdd"
        @save="handleClickSave"
        @cancel="handleClickCancel"
      />
    </el-form-item>
  </el-form>
  <additional-settings-table
    :data="ubicaciones"
    :columns="columns"
    :requestApi="ubicacionStore.findAllUbicacionHTTP"
    @edit="handleClickEdit"
    @delete="handleClickDelete"
  />
  <additional-settings-modal
    :visible="dialogVisible"
    component="AdditionalSettingsDeposits"
    title="Depósitos"
    :width="500"
    @closeModal="handleCloseModal"
  />
</template>

<script setup>
import {
  onMounted,
  ref,
  reactive,
  nextTick,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import AdditionalSettingsButtons from './additional_settings_buttons.vue';
import AdditionalSettingsModal from './additional_settings_modal.vue';
import AdditionalSettingsTable from './additional_settings_table.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomData from '@/shared/components/custom_data.vue';
import { useDepositoStore } from '@/stores/deposito/deposito_store';
import { useUbicacionStore } from '@/stores/ubicacion/ubicacion_store';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import IconAdd from '~icons/mdi/add';
import ubicacion_model from '@/shared/models/ubicacion_model.js';

const depositoStore = useDepositoStore();
const ubicacionStore = useUbicacionStore();
const { ubicaciones, ubicacion } = storeToRefs(ubicacionStore);

const columns = [
  { field: 'Codigo', label: 'ID', width: 75, sortable: true },
  { field: 'Descripcion', label: 'Descripción', width: 280, sortable: true },
];

const depositos = ref([]);
const disabled = ref(true);
const ruleFormRef = ref(null);
const descripcionInputRef = ref(null);

const form = reactive({ ...ubicacion_model });

const depositoWasSelected = (rule, value, callback) => {
  if (!value || !value.Codigo) {
    return callback(new Error('Seleccione un depósito'));
  }
  setTimeout(() => {
    callback();
  }, 1000);
};

const rules = reactive({
  Descripcion: [{ required: true, message: 'Ingrese la descripción', trigger: 'blur' }],
  Deposito: [{ validator: depositoWasSelected, trigger: 'blur' }],
});

const dialogVisible = ref(false);

const handleClickAddDeposito = () => {
  dialogVisible.value = true;
};

const handleCloseModal = () => {
  dialogVisible.value = false;
  fetchDepositos();
};

const handleClickAdd = () => {
  select();
  ubicacionStore.cleanUbicacion();
};

const handleClickEdit = (row) => {
  if (row.Codigo) {
    select(row);
  }
};

const select = (item) => {
  if (item) {
    Object.assign(form, item);
  } else {
    Object.assign(form, ubicacion_model);
  }
  disabled.value = false;
  nextTick(() => {
    descripcionInputRef.value.focus();
  });
};

const handleClickDelete = (row) => {
  if (row.Codigo) {
    ElMessageBox.confirm(`¿Quiere eliminar la ubicación ${row.Descripcion}?`, 'Confirmación', {
      confirmButtonText: 'Si',
      cancelButtonText: 'No',
      type: 'warning',
    })
      .then(() => {
        deleteUbicacion(row);
      })
      .catch((err) => {
        return;
      });
  }
};

const deleteUbicacion = async (row) => {
  try {
    useLoading().openLoading();

    await ubicacionStore.deleteUbicacionHTTP(row);
    clean();
    await ubicacionStore.firstRequest();
    useMessageSuccess().open(`La ubicación se eliminó exitosamente!`);
  } catch (error) {
    useMessageError().open(`Ocurrió un error al eliminar la ubicación!`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleClickSave = async () => {
  try {
    if (!ruleFormRef.value) return;

    const valid = await ruleFormRef.value.validate();

    if (!valid) {
      return;
    }
    await save();
  } catch (err) {}
};

const save = async () => {
  try {
    ubicacion.value = { ...form };

    useLoading().openLoading();

    await ubicacionStore.saveUbicacion();
    clean();
    await ubicacionStore.firstRequest();
    useMessageSuccess().open(`Los datos se guardaron exitosamente!`);
  } catch (error) {
    useMessageError().open(`Ocurrió un error al guardar los datos!`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleClickCancel = () => {
  clean();
};

const clean = () => {
  disabled.value = true;
  ubicacionStore.cleanUbicacion();
  ruleFormRef.value.clearValidate();
  Object.assign(form, ubicacion_model);
};

const fetchDepositos = async () => {
  await depositoStore.emptyList();
  depositos.value = await depositoStore.findAllDepositoAndReturn({ page: 0 });
};

onMounted(async () => {
  await ubicacionStore.firstRequest();
  await fetchDepositos();
});
</script>
